// HeroBand — the dark slate-900 dashboard hero panel: eyebrow/title/subtitle,
// an action slot, an optional KPI row and a decorative logomark watermark.
// This is the one sanctioned gradient surface outside the login page; the
// title is the page's h1. KPI values must already be complete numbers — the
// caller omits a KPI (or the whole row) rather than passing partial data.

import type { ReactNode } from "react";
import Sparkline from "./Sparkline";
import logomarkWhite from "../../assets/contractgen-logomark-white.svg";

export interface HeroBandKpi {
  label: string;
  value?: string;
  meta?: string;
  series?: number[];
}

interface HeroBandProps {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  action?: ReactNode;
  kpis?: HeroBandKpi[];
}

export default function HeroBand({
  eyebrow,
  title,
  subtitle,
  action,
  kpis,
}: HeroBandProps) {
  // A KPI with no value, no meta, and no (non-empty) series would render as
  // an orphaned label — an empty series in particular draws a blank sparkline
  // under a floating caption. Such bare KPIs are dropped rather than rendered.
  const renderableKpis = kpis?.filter(
    (kpi) =>
      kpi.value !== undefined ||
      kpi.meta !== undefined ||
      (kpi.series !== undefined && kpi.series.length > 0),
  );

  return (
    <div className="relative overflow-hidden rounded-xl px-8 py-7 text-white bg-slate-900 bg-hero-band shadow-hero-band dark:border dark:border-slate-700 dark:shadow-none">
      {/* Decorative watermark; hidden on mobile where it collides with text. */}
      <img
        src={logomarkWhite}
        alt=""
        aria-hidden="true"
        className="absolute -right-2.5 -bottom-7 w-[220px] opacity-10 pointer-events-none hidden sm:block"
      />
      <div className="relative z-10">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            {eyebrow && (
              <p className="mb-2 text-overline uppercase tracking-[0.08em] text-brand-300/95">
                {eyebrow}
              </p>
            )}
            <h1 className="text-[1.75rem] font-bold tracking-[-0.02em]">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-1.5 text-body text-slate-200/75">{subtitle}</p>
            )}
          </div>
          {action}
        </div>
        {renderableKpis && renderableKpis.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-9">
            {renderableKpis.map((kpi) => (
              <div
                key={kpi.label}
                className={kpi.series ? "flex flex-col justify-end" : undefined}
              >
                <p className="mb-1 text-caption uppercase tracking-[0.05em] text-brand-300/85">
                  {kpi.label}
                </p>
                {kpi.value !== undefined && (
                  <p className="text-[1.75rem] font-bold leading-none tabular-nums">
                    {kpi.value}
                  </p>
                )}
                {kpi.series && (
                  <Sparkline
                    data={kpi.series}
                    width={132}
                    height={36}
                    color="#c4b5fd"
                  />
                )}
                {kpi.meta && (
                  <p className="mt-1 text-caption text-slate-200/70">
                    {kpi.meta}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
